// assets/custom-total-results-count.js
(() => {
  const WRAPPER_SEL = '[data-testid="products-count"][data-custom-count-enabled="true"]';
  const TEXT_SEL = '[data-custom-count-text]';
  const GRID_SEL = '.custom-product-grid__list';
  const CARD_SEL = '.custom-product-grid__item';

  const cache = new Map();
  const abortBySection = new Map();
  let timer = null;

  function getEnabledWrappers() {
    return Array.from(document.querySelectorAll(WRAPPER_SEL));
  }

  function groupWrappersBySectionId() {
    const map = new Map();
    for (const wrap of getEnabledWrappers()) {
      const section = wrap.closest('results-list[section-id]');
      const sectionId = section?.getAttribute('section-id') || '';
      if (!sectionId) continue;
      if (!map.has(sectionId)) map.set(sectionId, []);
      map.get(sectionId).push(wrap);
    }
    return map;
  }

  function stableQueryFromCurrentUrl() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    params.delete('page');
    params.delete('section_id');

    const entries = [];
    for (const [k, v] of params.entries()) entries.push([k, v]);

    entries.sort((a, b) => {
      if (a[0] === b[0]) return a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0;
      return a[0] < b[0] ? -1 : 1;
    });

    return {
      pathname: url.pathname,
      query: entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&'),
    };
  }

  function getRunKey(sectionId) {
    const { pathname, query } = stableQueryFromCurrentUrl();
    return `${sectionId}|${pathname}?${query}`;
  }

  function setReadyForSection(wrappers, isReady) {
    for (const el of wrappers) {
      el.setAttribute('data-custom-count-ready', isReady ? 'true' : 'false');
    }
  }

  function formatFromTemplates(textEl, count) {
    const cutoff = 25000;
    const tplSing = textEl.dataset.countTemplateSingular || '';
    const tplPlur = textEl.dataset.countTemplatePlural || '';
    const tplCut = textEl.dataset.countTemplateCutoff || '';

    if (count > cutoff && tplCut) return tplCut.replace('__COUNT__', String(cutoff));
    if (count === 1 && tplSing) return tplSing.replace('__COUNT__', '1');

    const tpl = tplPlur || tplSing;
    return tpl ? tpl.replace('__COUNT__', String(count)) : String(count);
  }

  function applyCountForSection(wrappers, count) {
    for (const wrap of wrappers) {
      const textEl = wrap.querySelector(TEXT_SEL);
      if (!textEl) continue;
      textEl.textContent = formatFromTemplates(textEl, count);
    }
  }

  async function fetchSectionPage(sectionId, page, signal) {
    const url = new URL(window.location.href);

    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);

    const res = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const scope = doc.querySelector(`results-list[section-id="${sectionId}"]`) || doc;
    const grid = scope.querySelector(GRID_SEL);

    const cards = grid ? grid.querySelectorAll(CARD_SEL).length : 0;

    let lastPage = 1;
    if (grid) {
      const lp = parseInt(grid.getAttribute('data-last-page') || '1', 10);
      if (!Number.isNaN(lp) && lp > 0) lastPage = lp;
    }

    return { cards, lastPage };
  }

  async function computeTotal(sectionId, wrappers) {
    const key = getRunKey(sectionId);
    if (cache.has(key)) return cache.get(key);

    // abort laufende Berechnung für dieses sectionId
    const prev = abortBySection.get(sectionId);
    if (prev) prev.abort();

    const ctrl = new AbortController();
    abortBySection.set(sectionId, ctrl);

    // sofort skeleton
    setReadyForSection(wrappers, false);

    const first = await fetchSectionPage(sectionId, 1, ctrl.signal);
    let total = first.cards;
    const last = first.lastPage;

    for (let p = 2; p <= last; p += 1) {
      const next = await fetchSectionPage(sectionId, p, ctrl.signal);
      total += next.cards;
    }

    cache.set(key, total);
    return total;
  }

  async function run() {
    const grouped = groupWrappersBySectionId();
    if (!grouped.size) return;

    for (const [sectionId, wrappers] of grouped.entries()) {
      try {
        const total = await computeTotal(sectionId, wrappers);
        applyCountForSection(wrappers, total);
        setReadyForSection(wrappers, true);
      } catch (e) {
        if (e?.name === 'AbortError') continue;
        console.error('Custom count failed:', e);
        // nicht hängen lassen:
        setReadyForSection(wrappers, true);
      }
    }
  }

  function markLoadingAll() {
    const grouped = groupWrappersBySectionId();
    for (const wrappers of grouped.values()) setReadyForSection(wrappers, false);
  }

  function scheduleRun() {
    clearTimeout(timer);
    timer = setTimeout(run, 80);
  }

  function invalidateAndRecount() {
    cache.clear();
    for (const ctrl of abortBySection.values()) ctrl.abort();
    abortBySection.clear();

    // sofort skeleton anzeigen (keine alte Zahl)
    markLoadingAll();
    scheduleRun();
  }

  // --- History hooks: triggert NACH URL-Update (kein Race mehr) ---
  (function hookHistory() {
    const origPush = history.pushState;
    const origReplace = history.replaceState;

    history.pushState = function (...args) {
      const ret = origPush.apply(this, args);
      invalidateAndRecount();
      return ret;
    };

    history.replaceState = function (...args) {
      const ret = origReplace.apply(this, args);
      invalidateAndRecount();
      return ret;
    };
  })();

  // Initial
  document.addEventListener('DOMContentLoaded', () => {
    // direkt skeleton – kein “falscher” Text
    markLoadingAll();
    scheduleRun();
  });

  // Back/forward
  window.addEventListener('popstate', invalidateAndRecount);

  // BFCache restore
  window.addEventListener('pageshow', invalidateAndRecount);
})();